/**
 * Fixed world-width crop of the schema map around a station — the
 * Overview map preview (Figma 8597:41927) shows roughly the
 * neighbouring-stations context, which at the 4096-unit canvas is this
 * many canvas units across. Tune here, not per call site.
 */
export const MINI_MAP_VISIBLE_WORLD_WIDTH = 1100

export interface MiniMapCameraInput {
  /** Station centre in schema/canvas units. */
  readonly stationX: number
  readonly stationY: number
  /** Widget viewport in CSS pixels. */
  readonly cssWidth: number
  readonly cssHeight: number
  readonly dpr: number
}

export interface MiniMapCamera {
  /** Zoom for `buildViewMatrix` — canvas units → DEVICE pixels. */
  readonly deviceZoom: number
  /** Zoom for DOM overlays — canvas units → CSS pixels. */
  readonly cssZoom: number
  /** Viewport top-left in canvas units (shared by both zooms). */
  readonly offsetX: number
  readonly offsetY: number
  /** Projects a world point to CSS pixels inside the widget. */
  worldToCss(x: number, y: number): { x: number; y: number }
}

/**
 * Static camera of the read-only map preview: the station centred in a
 * fixed-world-width viewport. Pure math so the renderer draw, the
 * labels overlay and the marker bubble all derive from ONE transform —
 * the overlay-drift class of bug (MetroMapCanvas Bug 5) cannot start.
 */
export function miniMapCamera(input: MiniMapCameraInput): MiniMapCamera {
  const { stationX, stationY, cssWidth, cssHeight, dpr } = input
  const cssZoom = cssWidth / MINI_MAP_VISIBLE_WORLD_WIDTH
  const deviceZoom = cssZoom * dpr
  const visibleWorldHeight = cssHeight / cssZoom
  const offsetX = stationX - MINI_MAP_VISIBLE_WORLD_WIDTH / 2
  const offsetY = stationY - visibleWorldHeight / 2
  return {
    deviceZoom,
    cssZoom,
    offsetX,
    offsetY,
    worldToCss: (x, y) => ({ x: (x - offsetX) * cssZoom, y: (y - offsetY) * cssZoom }),
  }
}
